import { getRouteApi } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

import {
    discardMemoryBackfillWork,
    enrollMemoryBackfillOrganization,
    parkMemoryBackfillOrganizations,
    reclassifyParkedMemoryBackfillNotes,
    releaseMemoryBackfillItems,
    removeMemoryBackfillOrganizations,
    unparkMemoryBackfillOrganizations,
    useMemoryBackfillPreview,
} from '@apps/web/features/admin_agent_notes/api/memory_backfill_queries';
import {
    ApiError,
    type MemoryBackfillDiscardRequest,
    type MemoryBackfillDiscardResponse,
    type MemoryBackfillOrganizationRead,
    type MemoryBackfillReclassifyParkedRequest,
    type MemoryBackfillReclassifyParkedResponse,
    type MemoryBackfillReleaseRequest,
    type MemoryBackfillReleaseResponse,
} from '@libraries/typescript/api_client';

const route = getRouteApi('/_app/admin/agent-notes/');

type ConflictOrganization = {
    readonly organization_id: string;
    readonly organization_name?: string | null;
};

type UnparkConflictOrganization = {
    readonly organization_id: string;
    readonly parked_unreleased_item_count: number;
    readonly queued_reclassification_count: number;
};

function getErrorDetail(error: unknown): unknown {
    if (!(error instanceof ApiError)) {
        return null;
    }
    return (error.body as { readonly detail?: unknown } | undefined)?.detail;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function getConflictOrganizations(error: unknown): Array<ConflictOrganization> {
    const detail = getErrorDetail(error);
    if (!isRecord(detail) || !Array.isArray(detail['organizations'])) {
        return [];
    }
    return detail['organizations'].flatMap((organization) => {
        if (
            !isRecord(organization) ||
            typeof organization['organization_id'] !== 'string'
        ) {
            return [];
        }
        return [
            {
                organization_id: organization['organization_id'],
                organization_name:
                    typeof organization['organization_name'] === 'string'
                        ? organization['organization_name']
                        : null,
            },
        ];
    });
}

function getUnparkConflicts(error: unknown): Array<UnparkConflictOrganization> {
    const detail = getErrorDetail(error);
    if (!isRecord(detail) || !Array.isArray(detail['organizations'])) {
        return [];
    }
    return detail['organizations'].flatMap((organization) => {
        if (
            !isRecord(organization) ||
            typeof organization['organization_id'] !== 'string' ||
            typeof organization['parked_unreleased_item_count'] !== 'number' ||
            typeof organization['queued_reclassification_count'] !== 'number'
        ) {
            return [];
        }
        return [
            {
                organization_id: organization['organization_id'],
                parked_unreleased_item_count:
                    organization['parked_unreleased_item_count'],
                queued_reclassification_count:
                    organization['queued_reclassification_count'],
            },
        ];
    });
}

export function memoryBackfillErrorMessage(error: unknown): string {
    const detail = getErrorDetail(error);
    if (detail === 'organization_not_found') {
        return 'Organization not found.';
    }
    if (isRecord(detail)) {
        if (typeof detail['enrolled_organization_id'] === 'string') {
            return (
                'Another organization is already enrolled: ' +
                `${detail['enrolled_organization_id']}. Remove it before ` +
                'enrolling this organization.'
            );
        }
        if (detail['reason'] === 'review_preconditions') {
            return (
                'Remove the organization from enrollment and keep it parked ' +
                'before continuing.'
            );
        }
        if (detail['reason'] === 'stale_structured_item') {
            return (
                'A newer structured fact exists. The stale item can’t be ' +
                'retried.'
            );
        }
        const unparkConflicts = getUnparkConflicts(error);
        if (unparkConflicts.length > 0) {
            return unparkConflicts
                .map(
                    (organization) =>
                        `${organization.organization_id}: ` +
                        `${organization.parked_unreleased_item_count} parked ` +
                        'unreleased items, ' +
                        `${organization.queued_reclassification_count} ` +
                        'queued reclassifications',
                )
                .join('; ');
        }
        if (getConflictOrganizations(error).length > 0) {
            return 'Re-enable expanded memory before continuing';
        }
        if (typeof detail['message'] === 'string') {
            return detail['message'];
        }
    }
    if (typeof detail === 'string') {
        return detail;
    }
    return 'The memory backfill request failed. Please try again.';
}

export function useMemoryBackfill() {
    const search = route.useSearch();
    const navigate = route.useNavigate();
    const selectedOrganizationId = search.organization_id;
    const previewQuery = useMemoryBackfillPreview(selectedOrganizationId);
    const [isMutating, setIsMutating] = useState(false);
    const [mutationError, setMutationError] = useState<string | null>(null);

    const blockedOrganizations = useMemo(
        () => getConflictOrganizations(previewQuery.error),
        [previewQuery.error],
    );
    const organizations = previewQuery.data?.organizations ?? [];
    const selectedOrganization =
        organizations.find(
            (organization) =>
                organization.organization_id === selectedOrganizationId,
        ) ?? null;

    const selectOrganization = useCallback(
        (organizationId: string | undefined) => {
            void navigate({
                search: (previous) => ({
                    ...previous,
                    organization_id: organizationId,
                    note: undefined,
                    page: 1,
                }),
                replace: true,
            });
        },
        [navigate],
    );

    const refresh = useCallback(async () => {
        await previewQuery.mutate();
    }, [previewQuery]);

    const runMutation = useCallback(
        async <Result>(
            mutation: () => Promise<Result>,
        ): Promise<Result | null> => {
            setIsMutating(true);
            setMutationError(null);
            try {
                const result = await mutation();
                await previewQuery.mutate();
                return result;
            } catch (error) {
                setMutationError(memoryBackfillErrorMessage(error));
                return null;
            } finally {
                setIsMutating(false);
            }
        },
        [previewQuery],
    );

    const enroll = useCallback(
        (organizationId: string) =>
            runMutation(() =>
                enrollMemoryBackfillOrganization({
                    organization_id: organizationId,
                }),
            ),
        [runMutation],
    );
    const remove = useCallback(
        (organizationIds: Array<string>) =>
            runMutation(() =>
                removeMemoryBackfillOrganizations({
                    organization_ids: organizationIds,
                }),
            ),
        [runMutation],
    );
    const park = useCallback(
        (organizationIds: Array<string>) =>
            runMutation(() =>
                parkMemoryBackfillOrganizations({
                    organization_ids: organizationIds,
                }),
            ),
        [runMutation],
    );
    const unpark = useCallback(
        (organizationIds: Array<string>) =>
            runMutation(() =>
                unparkMemoryBackfillOrganizations({
                    organization_ids: organizationIds,
                }),
            ),
        [runMutation],
    );
    const release = useCallback(
        (
            body: MemoryBackfillReleaseRequest,
        ): Promise<MemoryBackfillReleaseResponse | null> =>
            runMutation(() => releaseMemoryBackfillItems(body)),
        [runMutation],
    );
    const reclassify = useCallback(
        (
            body: MemoryBackfillReclassifyParkedRequest,
        ): Promise<MemoryBackfillReclassifyParkedResponse | null> =>
            runMutation(() => reclassifyParkedMemoryBackfillNotes(body)),
        [runMutation],
    );
    const discard = useCallback(
        (
            body: MemoryBackfillDiscardRequest,
        ): Promise<MemoryBackfillDiscardResponse | null> =>
            runMutation(() => discardMemoryBackfillWork(body)),
        [runMutation],
    );

    const previewError =
        previewQuery.error === undefined || blockedOrganizations.length > 0
            ? null
            : memoryBackfillErrorMessage(previewQuery.error);

    return {
        selectedOrganizationId,
        selectedOrganization,
        organizations,
        blockedOrganizations,
        isLoading: previewQuery.isLoading,
        isRefreshing: previewQuery.isValidating,
        previewError,
        mutationError,
        isMutating,
        selectOrganization,
        clearMutationError: () => {
            setMutationError(null);
        },
        refresh,
        enroll,
        remove,
        park,
        unpark,
        release,
        reclassify,
        discard,
    };
}

export type MemoryBackfillOrganization =
    Readonly<MemoryBackfillOrganizationRead>;
